import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import InfiniteScroll from "react-infinite-scroll-component";
import { keepPreviousData } from "@tanstack/react-query";
import {
  Box,
  Button,
  TextField,
  Theme,
  TableContainer,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { TransactionV2 } from "@bn/api/apiv1";
import SearchIcon from "@icons/myicons/search.svg?react";
import DownloadIcon from "@icons/myicons/document-file-download.svg?react";
import { useRangeSelectControl, useTableSort } from "@hooks";
import {
  LoadingPlaceHolder,
  Table,
  TableSummary,
  AccountRowLoading,
} from "@components";
import {
  useDownloadTransactions,
  useInfiniteTransactionsV2,
} from "@api/v1/hooks/useTransactions";
import { filterTransactionsByPurposeAndInvestmentName } from "@helpers/filters";
import { createObjectUrl, downloadFile } from "@helpers/files";
import { AccountProtocolTableHead } from "./AccountProtocolTableHead";
import { AccountProtocolTableBody } from "./AccountProtocolTableBody";

const NoItemsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const TableContainerStyled = styled(TableContainer)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: theme.spacing(3),

  [theme.breakpoints.down(750)]: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
}));

interface AccountProtocolTableProps {
  summaryValue: number;
  accountId: number;
}

export const AccountProtocolTable = (props: AccountProtocolTableProps) => {
  const { summaryValue, accountId } = props;
  const theme = useTheme();

  const [filter, setFilter] = useState("");

  const isMoreSM = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));

  const { t } = useTranslation();

  const { range, intervalType, customIntervalSelect, selectInterval } =
    useRangeSelectControl({
      firstDate: new Date(),
    });

  const { sortField, sortDirection, sort } = useTableSort({
    field: "valueDate",
    direction: "DESC",
  });

  const {
    data: transactionsResponse,
    isFetching: isTableDataLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteTransactionsV2(
    {
      accountId: [accountId],
      minDate: range?.startDate?.toISOString(),
      maxDate: range?.endDate.toISOString(),
      order: [`${sortField} ${sortDirection?.toLowerCase()}`],
      include: ["bookings"],
      includeAdjustingEntries: true,
    },
    {
      enabled: Boolean(accountId),
      initialPageParam: { page: 0 },
      placeholderData: keepPreviousData,
      getNextPageParam: (lastPage) => {
        if (lastPage.paging?.pageCount === lastPage.paging?.page)
          return undefined;
        return (lastPage.paging?.page || 0) + 1;
      },
    }
  );
  const { mutate: downloadTransactions } = useDownloadTransactions(
    {
      format: "csv",
      accountId: [accountId],
      minDate: range?.startDate?.toISOString(),
      maxDate: range?.endDate.toISOString(),
      order: [`${sortField} ${sortDirection?.toLowerCase()}`],
    },
    {
      onSuccess: (data) => {
        const url = createObjectUrl(data, "text/csv");
        downloadFile(url, "transactions.csv");
      },
    }
  );

  const handleSort = (name: string) => {
    sort(name);
  };

  if (isTableDataLoading) {
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <AccountRowLoading />
        <AccountRowLoading />
        <AccountRowLoading />
      </Box>
    );
  }

  const transactions = (transactionsResponse?.pages || []).reduce(
    (acc, page) => {
      return [...acc, ...(page?.transactions || [])];
    },
    [] as TransactionV2[]
  );

  const filteredTransactions = filterTransactionsByPurposeAndInvestmentName(
    transactions,
    filter
  );

  return (
    <TableContainerStyled>
      <Header>
        <Typography variant="h6" component="h2">
          {t("singleAccountPage.protocolTable.title")}
        </Typography>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          gap={3}
          width="100%"
        >
          <TextField
            placeholder={t(
              "singleAccountPage.protocolTable.search.placeholder"
            )}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{
              width: "300px",
              [theme.breakpoints.down(750)]: { width: "100%" },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <Box color="icon.secondary" height="20px">
                    <SearchIcon width="20px" height="20px" />
                  </Box>
                ),
              },
            }}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon width="20px" height="20px" />}
            onClick={downloadTransactions}
            sx={{ flexShrink: 0 }}
          >
            {t("singleAccountPage.protocolTable.export")}
          </Button>
        </Box>
      </Header>
      <TableSummary
        summaryValue={summaryValue}
        currencySymbol="EUR"
        intervalType={intervalType}
        onSelectInterval={selectInterval}
        onSelectCustomInterval={customIntervalSelect}
      />
      <InfiniteScroll
        next={fetchNextPage}
        dataLength={filteredTransactions.length}
        hasMore={hasNextPage || false}
        loader={isFetchingNextPage ? <LoadingPlaceHolder /> : null}
      >
        <Table>
          {isMoreSM && (
            <AccountProtocolTableHead
              onSort={handleSort}
              sortField={sortField || ""}
              sortDirection={sortDirection || "NONE"}
            />
          )}
          <AccountProtocolTableBody data={filteredTransactions} />
        </Table>

        {!isTableDataLoading && !filteredTransactions.length && (
          <NoItemsContainer>
            <Typography variant="h5" color="textPrimary">
              {t("singleAccountPage.protocolTable.noItems")}
            </Typography>
          </NoItemsContainer>
        )}
      </InfiniteScroll>
    </TableContainerStyled>
  );
};
